import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import RackNav from '@/components/rack/RackNav';
import RackFooter from '@/components/rack/RackFooter';

/**
 * 文档页外壳 —— 支持 / 隐私 / 条款 / Cookie 四页共用。
 *
 * 这四页原来是上一版的浅色玻璃卡片（glass-effect + 渐变标题 + 图标徽章），
 * 和改版后的机架完全不是一台机器上的东西：从页脚点进去像跳到了另一个网站。
 * 这里把它们收进和案例页同一套版式——顶栏、面包屑、宋体标题、hairline 分栏，
 * 底部固定挂一条互相跳转的文档条，让四页之间不用回首页也能走。
 *
 * 条款编号（§01…）不是装饰：法务文本要能被引用（「按第 3 条」），编号是引用把手。
 * 支持页没有可引用的条款，所以它不用这套编号，改用问答行。
 */

export type DocLink = { to: string; label: string };

export const DOC_LINKS: DocLink[] = [
  { to: '/support', label: '支持' },
  { to: '/privacy', label: '隐私政策' },
  { to: '/terms', label: '服务条款' },
  { to: '/cookies', label: 'Cookie' },
];

export default function DocShell({
  code,
  title,
  updated,
  thesis,
  lede,
  description,
  path,
  children,
}: {
  /** 面包屑与眉标上的英文代号，全大写 */
  code: string;
  title: string;
  /** 「最后更新」右侧的日期文本；支持页没有版本概念时传空 */
  updated?: string;
  /** 一句话把这页的立场说完 */
  thesis: string;
  lede: string;
  description: string;
  path: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  return (
    <div className="min-h-screen bg-rack-ground font-hei text-rack-bone antialiased">
      <Helmet>
        <title>{`${title} · 阳仔AI`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`https://minddock.ai${path}`} />
      </Helmet>

      <RackNav />

      <main className="pt-24 sm:pt-28">
        <div className="mx-auto max-w-[1180px] px-6 sm:px-10">
          <nav className="flex items-center gap-2 font-readout text-[10px] uppercase tracking-[0.18em] text-rack-bone/25">
            <Link to="/" className="transition-colors hover:text-rack-bone/60">
              INDEX
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-rack-bone/45">{code}</span>
          </nav>

          <header className="mt-8 border-b border-rack-bone/12 pb-12">
            <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
              <h1 className="font-songti text-[clamp(1.7rem,4.6vw,2.5rem)] font-semibold tracking-[0.14em] text-rack-bone">
                {title}
              </h1>
              <span className="font-readout text-[10px] uppercase tracking-[0.3em] text-rack-bone/22">
                {code}
              </span>
            </div>

            <p className="mt-7 max-w-[32ch] font-songti text-[clamp(1.15rem,2.9vw,1.6rem)] font-semibold leading-[1.5] tracking-[0.08em] text-rack-bone/92">
              {thesis}
            </p>
            <p className="mt-5 max-w-[62ch] text-[13.5px] leading-[1.95] text-rack-bone/50">{lede}</p>

            {updated && (
              <p className="mt-7 font-readout text-[9.5px] uppercase tracking-[0.16em] text-rack-bone/26">
                LAST UPDATED · {updated}
              </p>
            )}
          </header>

          {children}

          {/* 收口：找人 + 四页互跳 */}
          <div className="mt-16 flex flex-col gap-7 border-t border-rack-bone/12 py-12 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-songti text-[clamp(1.1rem,2.6vw,1.45rem)] font-semibold leading-[1.5] tracking-[0.08em] text-rack-bone/92">
                这页没写到你的情况？
              </p>
              <p className="mt-3 max-w-[46ch] text-[13px] leading-[1.9] text-rack-bone/48">
                直接问我本人。微信 <span className="font-readout text-rack-bone/70">YangZaiAITech</span>
                ，或者发邮件。
              </p>
              <a
                href="mailto:contact@aiislove.tech"
                className="mt-6 inline-flex items-baseline gap-3 border-b border-rack-amber/30 pb-1.5 transition-colors hover:border-rack-amber focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-rack-amber"
              >
                <span className="font-readout text-[9.5px] uppercase tracking-[0.18em] text-rack-bone/28">
                  邮箱
                </span>
                <span className="font-readout text-[15px] tracking-[0.06em] text-rack-amber">
                  contact@aiislove.tech →
                </span>
              </a>
            </div>

            <nav
              className="flex flex-wrap gap-x-5 gap-y-2 sm:justify-end"
              aria-label="其它文档"
            >
              {DOC_LINKS.map((l) =>
                l.to === path ? (
                  <span
                    key={l.to}
                    aria-current="page"
                    className="text-[11.5px] text-rack-bone/70"
                  >
                    {l.label}
                  </span>
                ) : (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="text-[11.5px] text-rack-bone/45 transition-colors hover:text-rack-bone focus-visible:text-rack-bone focus-visible:outline-none"
                  >
                    {l.label}
                  </Link>
                ),
              )}
            </nav>
          </div>
        </div>
      </main>

      <RackFooter />
    </div>
  );
}

/** 条款：编号在左，正文在右。编号是引用把手，不是装饰 */
export function Clause({
  no,
  title,
  children,
}: {
  no: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-x-9 gap-y-3 border-b border-rack-bone/[0.07] py-8 lg:grid-cols-[220px_1fr]">
      <div className="flex items-baseline gap-3">
        <span className="font-readout text-[10px] tracking-[0.16em] text-rack-amber/70">{no}</span>
        <h2 className="font-songti text-[15.5px] font-semibold tracking-[0.1em] text-rack-bone/90">
          {title}
        </h2>
      </div>
      <div className="max-w-[62ch] space-y-4 text-[13px] leading-[1.95] text-rack-bone/55">
        {children}
      </div>
    </section>
  );
}
